import { authHeader } from "@roman/shared/lib/auth/authHeader";
import romanApi from "@roman/shared/lib/net/romanApi";
import type { CommentSortOption, ParentPredictionMarketComment } from "@roman/shared/types/Comment";
import type { Meta } from "@roman/shared/types/Meta";
import { useCallback } from "react";
import useSWRInfinite from "swr/infinite";

type ParentCommentResponse = {
  comments: ParentPredictionMarketComment[];
  meta: Meta;
};

type UseGetParentCommentsOptions = {
  initialPage?: number;
  initialPerPage?: number;
  infiniteScroll?: boolean;
  loadMoreButton?: boolean; // New option for load more button mode
  onlyMine?: boolean;
};

const fetcher = async (url: string): Promise<ParentCommentResponse> => {
  const headers = authHeader.get(); // Get auth headers if available
  return romanApi.get<ParentCommentResponse>(url, undefined, headers ? { headers } : undefined);
};

export const useGetParentComments = (
  predictionMarketId: string,
  sortOption: CommentSortOption = "COMMENTED_AT_DESC",
  onlyEntryAuthor = false,
  onlyMine = false,
  options: UseGetParentCommentsOptions = {},
): {
  data: ParentPredictionMarketComment[];
  size: number; // for infinite scroll
  error: Error | undefined;
  loading: boolean;
  refetch: () => Promise<ParentCommentResponse[] | undefined>;
  loadMore: () => Promise<void>;
  canLoadMore: boolean;
  totalCount: number;
} => {
  const { initialPerPage = 10 } = options;

  const getKey = (pageIndex: number, previousPageData: ParentCommentResponse[] | null) => {
    if (previousPageData && !previousPageData.length) {
      return null; // Reached the end
    }
    const page = pageIndex + 1;
    return `prediction-market/${predictionMarketId}/comments?page=${page}&per=${initialPerPage}&onlyEntryAuthor=${onlyEntryAuthor}&onlyMine=${onlyMine}&orderBy=${sortOption}`; // SWR key
  };

  const { data, error, isLoading, mutate, size, setSize } = useSWRInfinite<ParentCommentResponse, Error>(
    getKey,
    fetcher,
    {
      // Optimize SWR behavior for better UX
      revalidateOnFocus: true,
      revalidateOnReconnect: true, // Revalidate when connection is restored
      dedupingInterval: 5000, // Dedupe requests within 5 seconds
      errorRetryCount: 3, // Retry failed requests up to 3 times
      errorRetryInterval: 1000, // Wait 1 second between retries
      refreshInterval: 30000, // Refresh every 30 seconds
      parallel: true,
    },
  );

  // Load more handlers (works for both infinite scroll and load more button)
  const loadMore = useCallback(async () => {
    setSize((prev) => prev + 1);
  }, [setSize]);

  return {
    // Data
    data: data?.flatMap((page) => page.comments) ?? [],
    size, // for infinite scroll
    error,
    loading: isLoading,
    refetch: mutate,
    // Load more specific (works for both infinite scroll and load more button)
    loadMore,
    canLoadMore: data?.[data.length - 1]?.meta.next !== null,
    // Meta information
    totalCount: data?.[0]?.meta.totalCount ?? 0,
  };
};
